import { Spinner } from "@repo/ui/components/Base/Spinner";
import { LaserficheConnect } from "./LaserficheConnect";
import { LaserficheDirectoryTree } from "./LaserficheDirectoryTree";
import { LaserficheMetadataConfig } from "./LaserficheMetadataConfig";
import {
  laserficheIntegrationRepo,
  type UpdateLaserficheMetadata,
} from "@/repositories/laserficheIntegrationRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import type {
  LaserficheConnection,
  LaserficheInternalConfiguration,
  LaserficheMetadataConfigKey,
} from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { runSaveAll, SaveAllContext } from "./saveAllContext";
import { useConstComponent } from "@repo/ui/components/Base/hooks/useNestedComponent";
import { useState } from "react";
import { LaserficheInternalConfig } from "./LaserficheInternalConfig";
import { useTenantStore } from "@/stores/tenantStore";
import { useLaserficheGetIntegration } from "@/hooks/queryHooks/laserficheIntegrationHooks";

export interface LaserficheConfigurationProps {
  tenantId: string;
  teamId?: string | null;
}

export const LaserficheConfiguration = ({
  tenantId,
  teamId = null,
}: LaserficheConfigurationProps) => {
  const { data: laserfiche, loaded } = useLaserficheGetIntegration(
    tenantId,
    teamId
  );

  const tenant = useTenantStore((s) => s.tenant);

  const handleConnect = useFutureCallback(
    async (connection: LaserficheConnection) => {
      await laserficheIntegrationRepo.createConnection(
        tenantId,
        teamId,
        connection
      );
    }
  );

  const handleDirectory = useFutureCallback(async (directoryId: number) => {
    await laserficheIntegrationRepo.updateDirectory(
      tenantId,
      teamId,
      directoryId,
      "primary"
    );
  });

  const handleOrphanSignedDirectory = useFutureCallback(
    async (directoryId: number) => {
      await laserficheIntegrationRepo.updateDirectory(
        tenantId,
        teamId,
        directoryId,
        "orphanSigned"
      );
    }
  );

  const handleMetadata = useFutureCallback(
    async (meta: UpdateLaserficheMetadata) => {
      await laserficheIntegrationRepo.updateMetadata(tenantId, teamId, meta);
    }
  );

  const handleDeleteMetadata = useFutureCallback(
    async (metaKey: LaserficheMetadataConfigKey) => {
      await laserficheIntegrationRepo.deleteMetadata(tenantId, teamId, metaKey);
    }
  );

  const handleInternal = useFutureCallback(
    async (internal: LaserficheInternalConfiguration) => {
      await laserficheIntegrationRepo.updateInternal(
        tenantId,
        teamId,
        internal
      );
    }
  );

  const [loading, setLoading] = useState(false);

  const SaveAll = SaveAllContext.useAsParent();
  const Provider = useConstComponent(SaveAll.Provider);

  const handleSaveAll = useFutureCallback(async () => {
    setLoading(true);
    await runSaveAll(SaveAll);
    setLoading(false);
  });

  if (!loaded || !tenant) return <Spinner />;

  return (
    <div className="flex flex-col gap-5">
      <LaserficheConnect
        repositoryDisabled={!!laserfiche?.connection}
        initialValues={laserfiche?.connection ?? undefined}
        connectLabel={laserfiche?.connection ? "Update Connection" : "Connect"}
        onConnect={handleConnect}
      />

      {!!laserfiche?.connection && (
        <>
          <hr />

          <Button
            disabled={loading}
            variant="tertiary"
            className="self-start"
            onClick={handleSaveAll}
          >
            Save All
          </Button>

          <Provider>
            <LaserficheInternalConfig
              initialInternalConfig={laserfiche.internal}
              onSave={handleInternal}
            />

            <LaserficheDirectoryTree
              title="Configure Output Directory"
              tenantId={tenantId ?? ""}
              teamId={teamId}
              initialDirectory={laserfiche.directories?.primary?.directoryId}
              onSave={handleDirectory}
            />

            {tenant.featureFlags?.signatures && (
              <LaserficheDirectoryTree
                title="Directory for Signed Orphan Documents"
                tenantId={tenantId ?? ""}
                teamId={teamId}
                initialDirectory={
                  laserfiche.directories?.orphanSigned?.directoryId
                }
                onSave={handleOrphanSignedDirectory}
              />
            )}

            <LaserficheMetadataConfig
              tenantId={tenantId ?? ""}
              teamId={teamId}
              initialMetadata={laserfiche.metadata}
              onSave={handleMetadata}
              onDelete={handleDeleteMetadata}
            />
          </Provider>

          <Button
            disabled={loading}
            variant="tertiary"
            className="self-start"
            onClick={handleSaveAll}
          >
            Save All
          </Button>
        </>
      )}
    </div>
  );
};
